// Libs
import writeXlsxFile from "write-excel-file";
// Services
import { getParticipantsDynamicColumns } from "../../../utils/services/participants";
import { getAllForms } from "../../../utils/services/rsvp";
// Utils
import { formatDateTime } from "../../../utils/helpers/files/formatDate";

// Relatório dinâmico de participantes (RSVP).
// Propositalmente isolado do relatório simples (createXlsx.js): as colunas
// padrão são replicadas aqui para que mudanças em um relatório não afetem o
// outro.
// O usuário escolhe os formulários em /events/{id}/forms; as colunas saem do
// `json` de cada formulário escolhido e são enviadas para
// /login/loginbyevent/dynamic-columns/{id}, que devolve cada participante já
// com os valores dessas colunas.

// inputId dos componentes que não geram resposta (botão, label, captcha e
// link) — a ordem vem do array de components do FormMounted.
// PasswordDisplay (12) fica de fora de propósito: senha não vai para relatório.
const NON_FIELD_INPUT_IDS = [6, 10, 11, 12, 17];

// Mesmos aliases aplicados no envio das respostas (PublicTemplate/index.jsx),
// necessários para o attributeId bater com o que está gravado no banco.
const ATTR_ALIASES = {
  cellphone: "telefone",
  celular: "telefone",
};

// attributeId que não entram no relatório dinâmico por já existirem nas
// colunas padrão. Sempre em minúsculo — é assim que o attributeId é gravado.
// Acrescente novos itens aqui conforme a necessidade.
const IGNORED_ATTRIBUTE_IDS = [
  "nome",
  "email",
  "cpf",
  "telefone",
  "senha",
  "captcha",
  "titleqrcode",
  "codeqrcode",
  "infoacess",
  "grupos",
  "empresa",
  "cargo",
  "interessechat",
  "codigointerno1",
  "codigointerno2",
  "codigointerno3",
  "restricaohospedagem",
];

// Rótulo exibível do attributeId — mantém o texto como vem do banco,
// só sobe a primeira letra ("nomeCompleto" -> "NomeCompleto").
export function formatColumnLabel(attributeId) {
  const value = String(attributeId ?? "");
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Lê a página de participantes independente do envelope da API
// ({ data: { data: [...] } } ou { data: [...] }).
// Retorna null quando o formato não é reconhecido — normalmente um erro da API.
function extractParticipants(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return null;
}

// Extrai os attributeId dos campos de um formulário, na ordem em que aparecem.
// A regra é a mesma do envio das respostas: (uniqueField || inputName) em
// minúsculo — é assim que o attributeId é gravado em dynamic_form_responses.
function getFormColumns(form) {
  let structure = [];

  try {
    structure = form.json ? JSON.parse(form.json) : [];
  } catch (error) {
    return [];
  }

  if (!Array.isArray(structure)) return [];

  return structure
    .flatMap((row) => row.cols || [])
    .filter((col) => !NON_FIELD_INPUT_IDS.includes(col.inputId))
    .map((col) => {
      const rawId = (
        col.attributes?.uniqueField ||
        col.attributes?.inputName ||
        ""
      ).toLowerCase();
      return ATTR_ALIASES[rawId] || rawId;
    })
    .filter(
      (attributeId) =>
        attributeId && !IGNORED_ATTRIBUTE_IDS.includes(attributeId),
    );
}

// Busca os formulários do evento (paginado) com as colunas de cada um.
// A lista alimenta os checkboxes do modal do relatório dinâmico.
export async function loadEventForms({ eventId, limit = 100 }) {
  const forms = [];
  // teto de páginas para não entrar em loop infinito caso a API ignore o offset
  const maxPages = 50;
  let offset = 1;
  let keepFetching = true;

  while (keepFetching && offset <= maxPages) {
    const response = await getAllForms({ eventId, offset, limit });

    if (!response || (response.status && response.status >= 400)) {
      throw new Error("Falha ao buscar os formulários do evento.");
    }

    const page = response.data?.forms || [];
    forms.push(...page);

    keepFetching = page.length === limit;
    offset += 1;
  }

  return forms.map((form) => ({
    id: form.id,
    description: form.description || "Formulário sem descrição",
    columns: getFormColumns(form),
  }));
}

// Colunas padrão do relatório (mesmas do relatório simples)
function buildBaseSchema(idEvent) {
  // Retorna o vínculo do participante com o evento atual
  const getEventData = (participant) =>
    participant.eventos.find(({ event }) => event.id === idEvent);

  // Retorna os grupos do participante (id ou nome)
  const getGroups = (participant, field) =>
    participant.grupos
      .map((e) => (e.grupo.length > 0 ? e.grupo[0][field] : e.grupo[field]))
      .join(", ");

  return [
    { column: "ID", type: String, value: (p) => p.id },
    { column: "Nome", type: String, value: (p) => p.nome },
    { column: "Email", type: String, value: (p) => p.email },
    { column: "CPF", type: String, value: (p) => p.cpf },
    { column: "Telefone", type: String, value: (p) => p.telefone },
    {
      column: "Título QRCode",
      type: String,
      value: (p) => getEventData(p)?.titleQrCode,
    },
    {
      column: "Código QRCode",
      type: String,
      value: (p) => getEventData(p)?.codeQrCode,
    },
    {
      column: "Informações de Acesso",
      type: String,
      value: (p) => getEventData(p)?.infoAcess,
    },
    { column: "Empresa", type: String, value: (p) => getEventData(p)?.empresa },
    { column: "Cargo", type: String, value: (p) => getEventData(p)?.cargo },
    {
      column: "Bio Chat",
      type: String,
      value: (p) => getEventData(p)?.interesseChat,
    },
    {
      column: "Código Interno 1",
      type: String,
      value: (p) => getEventData(p)?.codigoInterno1,
    },
    {
      column: "Código Interno 2",
      type: String,
      value: (p) => getEventData(p)?.codigoInterno2,
    },
    {
      column: "Código Interno 3",
      type: String,
      value: (p) => getEventData(p)?.codigoInterno3,
    },
    {
      column: "Restrição de Hospedagem",
      type: String,
      value: (p) => getEventData(p)?.restricaoHospedagem,
    },
    {
      column: "Grupos/IDs",
      type: String,
      value: (p) => getGroups(p, "id"),
    },
    {
      column: "Grupos/Descrição",
      type: String,
      value: (p) => getGroups(p, "name"),
    },
    {
      column: "Data de Cadastro",
      type: String,
      value: (p) =>
        getEventData(p)?.criadoEm
          ? formatDateTime(getEventData(p)?.criadoEm)
          : "",
    },
    {
      column: "Primeiro Acesso",
      type: String,
      value: (p) =>
        getEventData(p)?.firstAccess
          ? formatDateTime(getEventData(p)?.firstAccess)
          : "",
    },
  ];
}

// Gera o xlsx com as colunas padrão + as colunas de cada formulário
// selecionado, e ao final de cada formulário a data/hora da resposta dele.
// O merge é feito pela API — cada participante vem com
// `dynamicValues: { [formId]: { createdAt, attributes: { [attributeId]: valor } } }`.
export async function createXlsxDynamic({
  idEvent,
  limit,
  search,
  status,
  countData,
  selectedForms,
  setFinishExport,
}) {
  const selectedColumns = selectedForms.flatMap((form) => form.columns);

  const schema = [
    ...buildBaseSchema(idEvent),
    ...selectedForms.flatMap((form) => [
      ...form.columns.map((attributeId) => ({
        column: formatColumnLabel(attributeId),
        type: String,
        value: (p) =>
          String(p.dynamicValues?.[form.id]?.attributes?.[attributeId] ?? ""),
      })),
      {
        column: "Data e Hora da resposta",
        type: String,
        value: (p) =>
          p.dynamicValues?.[form.id]?.createdAt
            ? formatDateTime(p.dynamicValues[form.id].createdAt)
            : "",
      },
    ]),
  ];

  const participants = [];
  const numberOfRequisitions = Math.ceil(countData / limit);

  for (let i = 1; i <= numberOfRequisitions; i += 1) {
    const response = await getParticipantsDynamicColumns({
      idEvent,
      offset: i,
      limit,
      search,
      status: status ? "ATIVO" : "INATIVO",
      columns: selectedColumns,
      forms: selectedForms.map((form) => form.id),
    });

    const page = extractParticipants(response);
    if (!page) {
      throw new Error(
        response?.message || "Falha ao buscar os participantes do relatório.",
      );
    }
    participants.push(...page);

    const progressBar = document.querySelector(".export-progress");
    const percentageBar = document.querySelector(".export-percentage");
    const percentage = ((i / numberOfRequisitions) * 100).toFixed(0);

    if (progressBar) progressBar.style.width = `${percentage}%`;
    if (percentageBar) percentageBar.innerHTML = `${percentage}%`;
  }

  if (participants.length > 0) {
    await writeXlsxFile(participants, {
      schema,
      fileName: "participantes.xlsx",
    });
  }

  setTimeout(() => {
    setFinishExport(true);
  }, 1000);
}
